import { isRouteErrorResponse, Link, useRouteError } from "react-router";
import { isApiError } from "../api/client";
import { useLogout } from "../api/queries";
import { useBrandName } from "../lib/brand";
import { useDocumentTitle } from "../lib/hooks";
import { Button, ButtonLink } from "../ui/Button";
import { ErrorState, EmptyState } from "../ui/EmptyState";
import { IconRefresh, IconSearch as IconCompass } from "../ui/icons";
import { AuthFrame } from "./auth/AuthLayout";

/** Full-page error, for when the app can't start (for example, the API is down). */
export function RootError() {
  const err = useRouteError();
  useDocumentTitle("Something went wrong");
  const api = isApiError(err) ? err : null;
  const brand = useBrandName();
  const title = api?.isNetwork ? `We can't reach ${brand}` : "Something went wrong";
  const message = api
    ? api.message
    : isRouteErrorResponse(err) && err.status === 404
      ? "That page doesn't exist."
      : "Something broke. Reload the page to try again.";
  if (!api && !isRouteErrorResponse(err)) console.error(err);

  return (
    <AuthFrame>
      <div className="card px-7 py-8 text-center sm:px-9">
        <h1 className="display text-[26px] leading-tight">{title}</h1>
        <p className="mx-auto mt-3 max-w-sm text-[15px] leading-relaxed text-body">{message}</p>
        <div className="mt-6 flex justify-center">
          <Button variant="primary" onClick={() => window.location.reload()} leading={<IconRefresh size={16} />}>
            Try again
          </Button>
        </div>
      </div>
    </AuthFrame>
  );
}

/** A page-level error inside the shell, so the sidebar stays usable. */
export function PageError() {
  const err = useRouteError();
  if (!isApiError(err)) console.error(err);
  return (
    <div className="card">
      <ErrorState error={err} onRetry={() => window.location.reload()} />
    </div>
  );
}

export function NotFound({ standalone }: { standalone?: boolean }) {
  useDocumentTitle("Page not found");
  const body = (
    <EmptyState
      icon={<IconCompass size={20} />}
      heading="h1"
      title="This page doesn't exist"
      action={
        <ButtonLink to="/" variant="primary">
          Go to Overview
        </ButtonLink>
      }
    >
      Check the address, or head back to the overview.
    </EmptyState>
  );
  if (!standalone) return <div className="card">{body}</div>;
  return (
    <AuthFrame>
      <div className="card">{body}</div>
    </AuthFrame>
  );
}

export function NoOrg() {
  const logout = useLogout();
  useDocumentTitle("No organization");
  return (
    <div className="card px-7 py-8 sm:px-9">
      <h1 className="display text-[26px] leading-tight">You're not in an organization yet</h1>
      <p className="mt-3 text-[15px] leading-relaxed text-body">
        No organization has added you yet. Ask an owner or admin to add your email.
      </p>
      <div className="mt-6 flex gap-3">
        <Button variant="secondary" loading={logout.isPending} onClick={() => logout.mutate(undefined, { onSettled: () => window.location.assign("/login") })}>
          Sign out
        </Button>
        <Link to="/" className="quiet-link self-center text-[14px]">
          Check again
        </Link>
      </div>
    </div>
  );
}
